/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Order Receipt & Electronic Tax Invoice Modal (Task 10.6.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  X,
  Printer,
  Download,
  Receipt,
  CheckCircle2,
  Lock,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useSubscriptionStore } from '../../stores/useSubscriptionStore';

export const OrderReceiptModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    isReceiptModalOpen,
    selectedOrder,
    setReceiptModalOpen,
    downloadInvoicePdf,
  } = useSubscriptionStore();

  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  if (!isReceiptModalOpen || !selectedOrder) return null;

  const handlePrint = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const handleDownload = () => {
    downloadInvoicePdf(selectedOrder.id);
    setDownloadSuccess(t('orders.downloadPdfSuccess'));
    setTimeout(() => setDownloadSuccess(null), 3000);
  };

  const formattedDate = new Date(selectedOrder.createdAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 rounded-2xl bg-surface border border-surface-border shadow-2xl space-y-6">
        {/* Modal Header */}
        <div className="flex items-start justify-between gap-4 border-b border-surface-border pb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="size-8 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
                <Receipt className="size-4" />
              </div>
              <h3 className="text-base font-bold font-heading text-foreground">
                {t('orders.receiptTitle')}
              </h3>
            </div>
            <p className="text-xs text-foreground-muted">
              {t('orders.receiptSubtitle')}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setReceiptModalOpen(false)}
            className="p-1.5 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted/80 transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Toast Feedback */}
        {downloadSuccess && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-xs font-medium">
            <CheckCircle2 className="size-4 text-emerald-500 flex-shrink-0" />
            <span>{downloadSuccess}</span>
          </div>
        )}

        {/* Invoice Paper Box */}
        <div className="p-5 rounded-xl bg-muted/30 border border-surface-border space-y-5">
          {/* Top Brand & Invoice Meta */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <div className="font-heading font-bold text-base text-foreground">
                Sophia Platform
              </div>
              <p className="text-[11px] text-foreground-muted">
                Four Elements Corp &bull; Cloudflare Edge-Native SAAS
              </p>
              <p className="text-[11px] text-foreground-muted font-mono mt-0.5">
                Tax ID: VN0317894210
              </p>
            </div>

            <div className="sm:text-right space-y-0.5">
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase bg-primary/10 text-primary border border-primary/20">
                {selectedOrder.invoiceNumber}
              </span>
              <p className="text-xs text-foreground-muted">
                {formattedDate}
              </p>
              <div className="pt-0.5">
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="size-3" />
                  <span>{selectedOrder.status.toUpperCase()}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Billed To & Gateway Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-surface-border text-xs">
            <div className="space-y-1">
              <span className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wider">
                {t('orders.billedTo')}
              </span>
              <p className="font-semibold text-foreground">
                {selectedOrder.companyName}
              </p>
              <p className="text-foreground-muted font-mono text-[11px]">
                {t('orders.vatNumber')}: {selectedOrder.companyVat}
              </p>
              <p className="text-foreground-muted text-[11px]">
                Workspace: {selectedOrder.workspaceId}
              </p>
            </div>

            <div className="space-y-1 sm:text-right">
              <span className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wider">
                {t('orders.colGateway')}
              </span>
              <p className="font-semibold text-foreground uppercase">
                {selectedOrder.gatewayCode}
              </p>
              <p className="text-foreground-muted font-mono text-[11px] truncate">
                {t('orders.gatewayTx')}: {selectedOrder.gatewayTransactionId}
              </p>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="pt-3 border-t border-surface-border">
            <table className="w-full text-xs text-left">
              <thead>
                <tr className="text-foreground-muted text-[11px] uppercase border-b border-surface-border">
                  <th className="pb-2">Description</th>
                  <th className="pb-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border/60">
                <tr>
                  <td className="py-2.5 font-medium text-foreground">
                    {selectedOrder.description}
                  </td>
                  <td className="py-2.5 text-right font-mono text-foreground font-semibold">
                    ${selectedOrder.amountPaid.toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Total Breakdown */}
          <div className="pt-3 border-t border-surface-border space-y-1 text-xs">
            <div className="flex justify-between text-foreground-muted">
              <span>{t('orders.subtotal')}</span>
              <span className="font-mono">
                ${(selectedOrder.amountPaid - selectedOrder.taxAmount).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-foreground-muted">
              <span>{t('orders.vatTax')}</span>
              <span className="font-mono">
                ${selectedOrder.taxAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm font-bold text-foreground pt-1 border-t border-surface-border">
              <span>{t('orders.totalAmount')}</span>
              <span className="font-mono text-primary">
                ${selectedOrder.amountPaid.toFixed(2)} {selectedOrder.currency}
              </span>
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-1.5 text-[11px] text-foreground-muted">
            <Lock className="size-3.5 text-primary" />
            <span>Encrypted e-Invoice Audit Record</span>
          </div>

          <div className="flex items-center gap-2 self-stretch sm:self-auto">
            <button
              type="button"
              onClick={handlePrint}
              className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-surface hover:bg-surface-hover text-foreground border border-surface-border text-xs font-medium shadow-2xs transition-colors"
            >
              <Printer className="size-3.5 text-foreground-muted" />
              <span>{t('orders.printReceipt')}</span>
            </button>

            <button
              type="button"
              onClick={handleDownload}
              className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary hover:bg-primary-hover text-primary-fg text-xs font-semibold shadow-xs transition-colors"
            >
              <Download className="size-3.5" />
              <span>{t('orders.downloadPdf')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
